import React, { ReactNode } from 'react'
import { Formik, Form, FormikProps, FormikHelpers } from 'formik'
import NiceModal, { useModal } from '@ebay/nice-modal-react'
import { notification } from 'antd'
import { useHistory } from 'react-router-dom'
import { Info, X, CheckCircle } from 'react-feather'

import {
  Modal,
  useShowMessage,
  useFeedbackOptions,
  RichText,
  useIsPepperDocsRoute,
} from '@pepper/ui'

import { updateAssignmentStatus } from '@pepper/utils/src/api/assignments/assignments'
import { getUserId } from '@pepper/utils/src/api/functions/authHelpers'
import { ASSIGNMENT_STATUS } from '@pepper/utils/src/constant/enum/assignment_status'
import { FeedbackCheckBoxList } from './FeedbackCheckBoxList'
import { TextAreaField } from 'components/FormFields/FormFields'
import { getInitialValues, getFeedbackValidationSchema, Values } from './schema'

import styles from './_.module.css'

type DeclinedSubmissionModalProps = {
  assignment: { id: string }
  handleOnCancelClick: () => void
  handleOnOkClick: () => void
  type: 'rework' | 'reject'
  subType?: 'revamp'
  projectCategory: ProjectCategory
  assignmentList?: string[]
  openNotificationAssignmentApproval?: (
    status: string,
    count: number,
    workStatus: string
  ) => void
  defaultComment?: string
}

type ReworkInputs = {
  id: number
  inputs: number[]
}

const DeclinedSubmissionModal = NiceModal.create<DeclinedSubmissionModalProps>(
  props => {
    const modal = useModal()
    const history = useHistory()
    const isPepperDocsRoute = useIsPepperDocsRoute()
    const userId = getUserId()
    const showMessage = useShowMessage()
    // TODO: pass actual type
    const feedbackOptions = useFeedbackOptions({
      type: props.projectCategory,
      getFlaggingOption: true,
    })
    const [api, contextHolder] = notification.useNotification()

    const getReworkAreasDataObject = (values: Values) => {
      const reworkOptions: ReworkInputs[] = []
      const flagOptions: ReworkInputs[] = []
      feedbackOptions.forEach(option => {
        if (
          Array.isArray(values.rework_points) &&
          values.rework_points.includes(option.key)
        ) {
          if (option.key === 'flag_creator') {
            flagOptions.push({
              id: option.id,
              inputs: (values[`${option.key}_options`] as string[]).map(
                (val: string) => Number.parseInt(val)
              ),
            })
          } else {
            reworkOptions.push({
              id: option.id,
              inputs: (values[`${option.key}_options`] as string[]).map(
                (val: string) => Number.parseInt(val)
              ),
            })
          }
        }
      })

      const reworkAndFlagOptions: ReworkInputs[] = [
        ...reworkOptions,
        ...flagOptions,
      ]

      return reworkAndFlagOptions
    }

    const fetchReworkItem = (values: Values, id: string) => {
      return updateAssignmentStatus({
        id,
        body: {
          assigner_role: 'client',
          assignee_id: userId || '',
          assigner_id: userId || '',
          reason:
            props.type === 'reject'
              ? values.feedback || ''
              : values.commentDetails?.comment || '',
          rework_attachments: values.commentDetails.attachments.map(
            (file: Attachment) => file.id
          ),
          rework_areas: values.rework_points || [],
          rework_areas_data: getReworkAreasDataObject(values),
          status:
            props.type === 'reject'
              ? ASSIGNMENT_STATUS.CLI_RJT.id
              : ASSIGNMENT_STATUS.CLI_RWK.id,
        },
      })
    }

    const reworkNotificationPopUp = () => {
      if (api?.info) {
        api.info({
          className: styles.toastAlert,
          duration: 3,
          message: (
            <span className={styles.toastTextSecond}>
              Assignment has been marked as rework
            </span>
          ),
          icon: <CheckCircle className={styles.toastCircle} />,
          closeIcon: <X className={styles.toastCircle} />,
          placement: 'topRight',
        })
      }
    }
    const rejectNotificationPopUp = () => {
      if (api?.info) {
        api.info({
          className: styles.toastSuccess,
          duration: 3,
          message: (
            <span className={styles.toastText}>
              {`The assignment has been rejected. We're now reviewing it for cancellation & refund.`}
            </span>
          ),
          icon: <Info className={styles.toastInfo} />,
          closeIcon: <X className={styles.toastInfo} />,
          placement: 'topRight',
        })
      }
    }

    const handleOnConfirm = async (
      values: Values,
      action: FormikHelpers<Values>
    ) => {
      const { assignmentList, openNotificationAssignmentApproval } = props

      if (assignmentList && assignmentList.length > 0) {
        const statusUpdateSet = await Promise.all(
          assignmentList.map(async (assignmentItemID: string) => {
            return fetchReworkItem(values, assignmentItemID)
          })
        )
        if (statusUpdateSet?.length > 0) {
          let okStatusCount = 0
          statusUpdateSet.forEach(statusItem => {
            if (statusItem.status !== 'ok') {
              return showMessage({ type: 'error', message: statusItem.error })
            } else {
              okStatusCount++
            }
          })
          if (okStatusCount > 0 && openNotificationAssignmentApproval) {
            openNotificationAssignmentApproval('ok', okStatusCount, 'rework')
          }
        }
      } else {
        const reworkResponse = await fetchReworkItem(
          values,
          props.assignment.id
        )
        if (reworkResponse.status !== 'ok') {
          return showMessage({ type: 'error', message: reworkResponse.error })
        } else if (openNotificationAssignmentApproval) {
          openNotificationAssignmentApproval('ok', 1, 'rework')
        }
      }

      if (props.type === 'reject') {
        rejectNotificationPopUp()
      }
      if (
        props.type === 'rework' &&
        !openNotificationAssignmentApproval === true
      ) {
        reworkNotificationPopUp()
      }

      props.handleOnOkClick()
      props.handleOnCancelClick()
      action.resetForm()
      if (isPepperDocsRoute) {
        window.close()
      }
    }

    const handleReworkPointChange = (
      value: string,
      action: FormikProps<Values>
    ) => {
      // todo: pass input id from utils
      const prevValue = { ...action.values }
      const _points = [...prevValue.rework_points]
      if (_points.includes(value)) {
        const filtered = _points.filter(val => val !== value)
        action.setValues({
          ...prevValue,
          rework_points: filtered,
          [`${value}_options`]: [],
        })
      } else {
        action.setFieldValue('rework_points', [..._points, value])
      }
    }

    const response = getInitialValues(feedbackOptions)
    if (props.defaultComment)
      response.commentDetails.comment = props.defaultComment

    const onSubmit = (values: Values, action: FormikHelpers<Values>) => {
      if (props.type === 'reject') {
        return handleOnConfirm(
          {
            ...values,
            flag_creator_options: ['94'],
            rework_points: [...values.rework_points, 'flag_creator'],
          },
          action
        )
      } else if (props.subType === 'revamp') {
        return handleOnConfirm(
          {
            ...values,
            flag_creator_options: ['93'],
            rework_points: [...values.rework_points, 'flag_creator'],
          },
          action
        )
      }
      return handleOnConfirm(values, action)
    }

    const getIsDisabled = ({ isValid, values }: FormikProps<Values>) => {
      if (!isValid) return true

      if (props.projectCategory !== 'others') {
        return values.rework_points.length === 0
      }

      return false
    }

    return (
      <React.Fragment>
        {contextHolder}
        <Formik
          enableReinitialize={true}
          initialValues={response}
          onSubmit={(values, action) => onSubmit(values, action)}
          validationSchema={getFeedbackValidationSchema(
            feedbackOptions,
            props.projectCategory
          )}
          validateOnMount={true}
        >
          {(formik: FormikProps<Values>): ReactNode => {
            return (
              <>
                <Modal
                  cancelButtonProps={{ className: 'button-md' }}
                  className={styles.declinedModal}
                  closable={true}
                  okText="Confirm"
                  onCancel={() => {
                    props.handleOnCancelClick()
                    formik.resetForm()
                    modal.hide()
                  }}
                  onOk={() => {
                    formik.submitForm()
                    modal.hide()
                  }}
                  okButtonProps={{
                    htmlType: 'submit',
                    loading: formik.isSubmitting,
                    className: 'button-md',
                    disabled: getIsDisabled(formik),
                  }}
                  title={
                    props.type === 'rework'
                      ? 'Please select issues you need resolved in the rework'
                      : 'Reject Submission'
                  }
                  visible={modal.visible}
                >
                  <div>
                    <Form className={styles.form}>
                      {props.projectCategory !== 'others' && (
                        <div className={styles.header}>
                          {props.type === 'rework'
                            ? `What were the major quality concerns for which you are sending this assignment for reworking? Please select the most appropriate quality lapses under these ${
                                props.projectCategory === 'text'
                                  ? 'five'
                                  : 'six'
                              } parameters.`
                            : 'Please explain the quality issues that led to this rejection.'}
                        </div>
                      )}
                      <section className={styles.formSection}>
                        {feedbackOptions.map(option => {
                          return (
                            option.key !== 'flag_creator' && (
                              <FeedbackCheckBoxList
                                option={option}
                                handleReworkPointChange={
                                  handleReworkPointChange
                                }
                                formik={formik}
                                type={props.type}
                              />
                            )
                          )
                        })}
                        <div className={styles.commentBox}>
                          {props.type === 'rework' ? (
                            <div>
                              <div style={{ marginBottom: '5px' }}>
                                Add comment
                              </div>
                              <RichText
                                mentionData={[]}
                                isAttachFile={true}
                                maxLength={1500}
                                onChange={(_values, plainText) => {
                                  if (typeof _values !== 'string') {
                                    formik.setFieldValue('commentDetails', {
                                      comment: _values?.richText,
                                      attachments: _values.attachments,
                                      plainText:
                                        plainText ||
                                        formik?.values?.commentDetails
                                          ?.plainText,
                                    })
                                  }
                                }}
                                fieldData={{
                                  richText:
                                    formik.values.commentDetails?.comment || '',
                                  attachments:
                                    formik.values.commentDetails?.attachments ||
                                    [],
                                  loomUrls: [],
                                }}
                              />
                            </div>
                          ) : (
                            <TextAreaField
                              name="feedback"
                              label="Add Comment"
                              required={false}
                              fieldProps={{
                                className: styles.commentInputBox,
                                showCount: true,
                                maxLength: 1800,
                              }}
                            />
                          )}
                        </div>
                      </section>
                    </Form>
                  </div>
                </Modal>
              </>
            )
          }}
        </Formik>
      </React.Fragment>
    )
  }
)

export default DeclinedSubmissionModal
